import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  clearAdminToken,
  fetchDemoRequests,
  fetchLandingStats,
  getAdminToken,
  type LandingDemoRequestRow,
  type LandingStatsResponse,
} from '@/lib/api';
import '@/styles/admin.css';

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-UY', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatShortDate(iso: string): string {
  const parts = iso.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}`;
  return iso;
}

function NamedList({
  empty,
  items,
}: {
  empty: string;
  items: { label: string; count: number }[];
}) {
  if (items.length === 0) return <p className="admin-empty">{empty}</p>;
  return (
    <ul className="admin-list">
      {items.map((item) => (
        <li key={item.label}>
          <span>{item.label}</span>
          <strong>{item.count}</strong>
        </li>
      ))}
    </ul>
  );
}

export function AdminStatsPage() {
  const nav = useNavigate();
  const token = getAdminToken();
  const [days, setDays] = useState(7);
  const [stats, setStats] = useState<LandingStatsResponse | null>(null);
  const [demos, setDemos] = useState<LandingDemoRequestRow[]>([]);
  const [demoTotal, setDemoTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [statsData, demoData] = await Promise.all([
        fetchLandingStats(token, days),
        fetchDemoRequests(token, 50, 0),
      ]);
      setStats(statsData);
      setDemos(demoData.rows);
      setDemoTotal(demoData.total);
    } catch (err) {
      if (err instanceof Error && err.message === 'SESSION_EXPIRED') {
        nav('/admin/login', { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : 'No se pudieron cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  }, [token, days, nav]);

  useEffect(() => {
    void load();
  }, [load]);

  const chartMax = useMemo(() => {
    if (!stats?.daily.length) return 1;
    return Math.max(1, ...stats.daily.map((d) => d.pageViews));
  }, [stats]);

  const scrollMax = useMemo(() => {
    if (!stats?.scrollDepth?.length) return 1;
    return Math.max(1, ...stats.scrollDepth.map((d) => d.sessions));
  }, [stats]);

  const funnelMax = useMemo(() => {
    if (!stats?.formFunnel?.length) return 1;
    return Math.max(1, ...stats.formFunnel.map((d) => d.sessions));
  }, [stats]);

  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }

  const logout = () => {
    clearAdminToken();
    nav('/admin/login', { replace: true });
  };

  const topCtas = stats?.topCtas ?? [];
  const topCtasByRegion = stats?.topCtasByRegion ?? [];
  const topSections = stats?.topSections ?? [];
  const topPaths = stats?.topPaths ?? [];
  const topUtm = stats?.topUtmSources ?? [];
  const topCountries = stats?.topCountries ?? [];
  const topClickCountries = stats?.topClickCountries ?? [];
  const scrollDepth = stats?.scrollDepth ?? [];
  const formFunnel = stats?.formFunnel ?? [];

  return (
    <div className="admin-root">
      <div className="admin-shell">
        <header className="admin-header">
          <div>
            <h1 className="admin-title">Estadísticas de la landing</h1>
            <p className="admin-subtitle">Visitas, comportamiento y conversiones a demo</p>
          </div>
          <div className="admin-toolbar">
            <select
              className="admin-select"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              aria-label="Período"
            >
              <option value={7}>Última semana</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
            </select>
            <button type="button" className="admin-btn admin-btn-ghost" onClick={() => void load()}>
              Actualizar
            </button>
            <button type="button" className="admin-btn admin-btn-ghost" onClick={logout}>
              Salir
            </button>
            <Link to="/" className="admin-btn admin-btn-ghost" style={{ textDecoration: 'none' }}>
              Sitio
            </Link>
          </div>
        </header>

        {error ? <p className="admin-error">{error}</p> : null}
        {loading && !stats ? <p className="admin-empty">Cargando…</p> : null}

        {stats ? (
          <>
            <div className="admin-kpi-grid">
              <div className="admin-kpi">
                <div className="admin-kpi-label">Visitas hoy</div>
                <div className="admin-kpi-value">{stats.summary.pageViewsToday}</div>
              </div>
              <div className="admin-kpi">
                <div className="admin-kpi-label">Visitas ({days} días)</div>
                <div className="admin-kpi-value">{stats.summary.pageViewsTotal}</div>
                <div className="admin-kpi-hint">{stats.summary.uniqueVisitors} visitantes únicos</div>
              </div>
              <div className="admin-kpi">
                <div className="admin-kpi-label">Demos hoy</div>
                <div className="admin-kpi-value">{stats.summary.demoRequestsToday}</div>
              </div>
              <div className="admin-kpi">
                <div className="admin-kpi-label">Demos ({days} días)</div>
                <div className="admin-kpi-value">{stats.summary.demoRequestsTotal}</div>
                <div className="admin-kpi-hint">Total histórico: {demoTotal}</div>
              </div>
              <div className="admin-kpi">
                <div className="admin-kpi-label">Conversión</div>
                <div className="admin-kpi-value">{stats.summary.conversionRate}%</div>
                <div className="admin-kpi-hint">Demos / visitas en el período</div>
              </div>
              {typeof stats.summary.wizardOpens === 'number' ? (
                <div className="admin-kpi">
                  <div className="admin-kpi-label">Wizard mobile</div>
                  <div className="admin-kpi-value">{stats.summary.wizardOpens}</div>
                  <div className="admin-kpi-hint">Sesiones que abrieron el formulario</div>
                </div>
              ) : null}
            </div>

            <div className="admin-grid-2">
              <section className="admin-card">
                <h2>Visitas por día</h2>
                {stats.daily.every((d) => d.pageViews === 0) ? (
                  <p className="admin-empty">Sin visitas registradas en este período.</p>
                ) : (
                  <div
                    className={`admin-chart${days <= 14 ? ' admin-chart--weekly' : ''}`}
                    role="img"
                    aria-label="Visitas diarias"
                  >
                    {stats.daily.map((d) => (
                      <div
                        key={d.date}
                        className="admin-chart-bar-wrap"
                        title={`${d.date}: ${d.pageViews} visitas · ${d.uniqueVisitors} únicos · ${d.demoRequests} demos`}
                      >
                        <span className="admin-chart-value">{d.pageViews}</span>
                        <div
                          className="admin-chart-bar"
                          style={{ height: `${Math.max(4, (d.pageViews / chartMax) * 140)}px` }}
                        />
                        <span className="admin-chart-label">{formatShortDate(d.date)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="admin-card">
                <h2>Origen del tráfico</h2>
                {stats.topReferrers.length === 0 ? (
                  <p className="admin-empty">Sin datos de referencia.</p>
                ) : (
                  <ul className="admin-list">
                    {stats.topReferrers.map((r) => (
                      <li key={r.referrer}>
                        <span>{r.referrer}</span>
                        <strong>{r.count}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <div className="admin-grid-2" style={{ marginTop: '1.25rem' }}>
              <section className="admin-card">
                <h2>Visitas por país / región</h2>
                <p className="admin-card-hint">Inferido por zona horaria/idioma del navegador o cabecera CloudFront.</p>
                {topCountries.length === 0 ? (
                  <p className="admin-empty">Sin datos de país aún.</p>
                ) : (
                  <ul className="admin-list">
                    {topCountries.map((c) => (
                      <li key={c.label}>
                        <span>{c.label}</span>
                        <strong>{c.count}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="admin-card">
                <h2>Clicks por país / región</h2>
                <p className="admin-card-hint">De dónde vienen los clics a CTAs.</p>
                {topClickCountries.length === 0 ? (
                  <p className="admin-empty">Sin clics geolocalizados aún.</p>
                ) : (
                  <ul className="admin-list">
                    {topClickCountries.map((c) => (
                      <li key={c.label}>
                        <span>{c.label}</span>
                        <strong>{c.count}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <div className="admin-grid-2" style={{ marginTop: '1.25rem' }}>
              <section className="admin-card">
                <h2>Secciones más vistas</h2>
                <p className="admin-card-hint">Dónde llega el scroll del usuario (por sección con id).</p>
                {topSections.length === 0 ? (
                  <p className="admin-empty">Aún no hay vistas de sección. Aparecen con visitas nuevas.</p>
                ) : (
                  <ul className="admin-list">
                    {topSections.map((s) => (
                      <li key={s.section}>
                        <span>
                          #{s.section}
                          <span className="admin-list-meta"> · {s.uniqueSessions} sesiones</span>
                        </span>
                        <strong>{s.views}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="admin-card">
                <h2>CTAs más clickeados</h2>
                <p className="admin-card-hint">Qué botones impulsan más acción.</p>
                <NamedList empty="Sin clics de CTA en el período." items={topCtas} />
              </section>
            </div>

            <section className="admin-card" style={{ marginTop: '1.25rem' }}>
              <h2>CTA × región</h2>
              <p className="admin-card-hint">Combinación de país y botón clickeado.</p>
              <NamedList empty="Sin clics con región aún." items={topCtasByRegion} />
            </section>

            <div className="admin-grid-2" style={{ marginTop: '1.25rem' }}>
              <section className="admin-card">
                <h2>Profundidad de scroll</h2>
                <p className="admin-card-hint">Sesiones únicas que alcanzaron cada hito.</p>
                {scrollDepth.every((d) => d.sessions === 0) ? (
                  <p className="admin-empty">Sin datos de scroll.</p>
                ) : (
                  <div className="admin-chart admin-chart--compact" aria-hidden>
                    {scrollDepth.map((d) => (
                      <div key={d.percent} className="admin-chart-bar-wrap">
                        <span className="admin-chart-value">{d.sessions}</span>
                        <div
                          className="admin-chart-bar admin-chart-bar--accent"
                          style={{ height: `${Math.max(4, (d.sessions / scrollMax) * 100)}px` }}
                        />
                        <span className="admin-chart-label">{d.percent}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="admin-card">
                <h2>Embudo de demo</h2>
                <p className="admin-card-hint">Pasos del formulario hasta agendar.</p>
                {formFunnel.every((d) => d.sessions === 0) ? (
                  <p className="admin-empty">Sin actividad de formulario.</p>
                ) : (
                  <ul className="admin-funnel">
                    {formFunnel.map((step) => (
                      <li key={step.step}>
                        <div className="admin-funnel-meta">
                          <span>{step.label}</span>
                          <strong>{step.sessions}</strong>
                        </div>
                        <div className="admin-funnel-track">
                          <div
                            className="admin-funnel-fill"
                            style={{ width: `${Math.max(2, (step.sessions / funnelMax) * 100)}%` }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <div className="admin-grid-2" style={{ marginTop: '1.25rem' }}>
              <section className="admin-card">
                <h2>Páginas</h2>
                <NamedList empty="Sin rutas registradas." items={topPaths} />
              </section>
              <section className="admin-card">
                <h2>UTM source</h2>
                <NamedList empty="Sin campañas UTM en el período." items={topUtm} />
              </section>
            </div>

            <section className="admin-card" style={{ marginTop: '1.25rem' }}>
              <h2>Últimas solicitudes de demo</h2>
              {demos.length === 0 ? (
                <p className="admin-empty">Todavía no hay solicitudes registradas.</p>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Contacto</th>
                        <th>Empresa</th>
                        <th>Reunión</th>
                        <th>Email</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demos.map((row) => (
                        <tr key={row.id}>
                          <td>{formatDate(row.createdAt)}</td>
                          <td>
                            {row.name}
                            {row.phone ? (
                              <>
                                <br />
                                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{row.phone}</span>
                              </>
                            ) : null}
                          </td>
                          <td>{row.company}</td>
                          <td>
                            {row.scheduledAt ? (
                              <>
                                {formatDate(row.scheduledAt)}
                                {row.meetUrl ? (
                                  <>
                                    <br />
                                    <a href={row.meetUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem' }}>
                                      Meet
                                    </a>
                                  </>
                                ) : null}
                              </>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>—</span>
                            )}
                          </td>
                          <td>
                            <a href={`mailto:${row.email}`}>{row.email}</a>
                          </td>
                          <td>
                            {row.emailSent ? (
                              <span className="admin-badge-ok">Email enviado</span>
                            ) : (
                              <span className="admin-badge-warn">Sin email</span>
                            )}
                            {row.confirmationEmailSent ? (
                              <>
                                <br />
                                <span className="admin-badge-ok" style={{ marginTop: 4, display: 'inline-block' }}>
                                  Confirmación
                                </span>
                              </>
                            ) : row.scheduledAt ? (
                              <>
                                <br />
                                <span className="admin-badge-warn" style={{ marginTop: 4, display: 'inline-block' }}>
                                  Sin confirmación
                                </span>
                              </>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
}
